import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatDateTime } from "@/lib/format";

type Organisation = components["schemas"]["OrganisationAdminResponse"];
type OrganisationSearchHit = components["schemas"]["OrganisationAdminSearchHit"];

// Trigram search returns top matches rather than a page, so searching hides the pager.
const SEARCH_LIMIT = 20;

export function OrganisationsListPage() {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);
  const searchTerm = debouncedSearch.trim();
  const searching = searchTerm.length > 0;

  const pagination = useCursorPagination({ initialLimit: 20 });

  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [hits, setHits] = useState<OrganisationSearchHit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const prevSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      pagination.reset();
    }
  }, [debouncedSearch, pagination.reset]);

  // Fuzzy search (id, name, email, tax and registration numbers), unlike the paged list's exact-name filter.
  useEffect(() => {
    if (!searching) return;
    let cancelled = false;

    async function searchOrganisations() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/organisations/search", {
        params: { query: { q: searchTerm, limit: SEARCH_LIMIT } },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to search organisations.");
      } else {
        setHits(data ?? []);
      }
      setLoading(false);
    }

    searchOrganisations();
    return () => {
      cancelled = true;
    };
  }, [searching, searchTerm]);

  useEffect(() => {
    if (searching) return;
    let cancelled = false;

    async function fetchOrganisations() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/organisations", {
        params: {
          query: {
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load organisations.");
      } else {
        setOrganisations(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchOrganisations();
    return () => {
      cancelled = true;
    };
  }, [searching, pagination.cursor, pagination.limit]);

  // One row shape for both modes: the search hit carries no createdAt, so the trailing column
  // shows the contact email instead.
  const rows = searching
    ? hits.map((h) => ({ id: h.id, name: h.name, clientId: h.clientId, trailing: h.contactEmail ?? "-" }))
    : organisations.map((o) => ({
        id: o.id,
        name: o.name,
        clientId: o.clientId,
        trailing: formatDateTime(o.createdAt),
      }));

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <h1 className="mb-6 text-2xl font-bold">Organisations</h1>

      <div className="mb-4 flex items-center gap-3">
        <Input
          placeholder="Search by name, ID, email, tax no..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-sm"
        />
        {loading && <Spinner className="size-5" />}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Client ID</TableHead>
              <TableHead>{searching ? "Contact email" : "Created"}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground h-24 text-center">
                  {searching
                    ? `No organisations matching "${searchTerm}".`
                    : "No organisations found."}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((org) => (
                <TableRow key={org.id}>
                  <TableCell className="font-medium">
                    <Link to={`/organisations/${org.id}`} className="hover:underline">
                      {org.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/organisations/${org.id}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-secondary/80">
                        {org.id}
                      </Badge>
                    </Link>
                  </TableCell>
                  <TableCell>
                    {org.clientId && (
                      <Link to={`/clients/${org.clientId}`}>
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {org.clientId}
                        </Badge>
                      </Link>
                    )}
                  </TableCell>
                  <TableCell>{org.trailing}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {searching ? (
        <p className="text-muted-foreground mt-3 text-xs">
          Showing the closest matches across every client
          {hits.length === SEARCH_LIMIT ? ` (top ${SEARCH_LIMIT})` : ""}.
        </p>
      ) : (
        <PaginationBar
          hasPrevious={pagination.hasPrevious}
          hasNext={pagination.hasMore}
          isLoading={loading}
          limit={pagination.limit}
          onPrevious={pagination.goPrevious}
          onNext={pagination.goNext}
          onLimitChange={pagination.setLimit}
        />
      )}
    </div>
  );
}
